import Service from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import {
  setValueForPath,
  valueFromPath,
} from 'my-phorest/utils/value-from-path';
import diff from 'microdiff';
import { safeClone } from 'my-phorest/utils/safe-clone';

function normalizeForComparison(edited, original) {
  if (typeof edited !== 'object' || edited === null || Array.isArray(edited)) {
    return edited;
  }

  original = original || {};

  const cleaned = { ...edited };

  for (const key of Object.keys(cleaned)) {
    const value = cleaned[key];
    const originalHasKey = Object.prototype.hasOwnProperty.call(original, key);

    if (
      (value === undefined || value === null || value === '') &&
      !originalHasKey
    ) {
      delete cleaned[key];
    } else {
      cleaned[key] = normalizeForComparison(value, original[key]);
    }
  }

  return cleaned;
}

export default class TableEditingService extends Service {
  @tracked editedResourcesByKeyMap = {};
  originalResourcesByKeyMap = {};
  keyProperty = ['id']; // For tables with no id, can use another property e.g. ['product', 'id']

  @action
  initialize(keyProperty = ['id']) {
    this.reset();
    this.keyProperty = keyProperty;
  }

  get hasChanges() {
    return Object.keys(this.editedResourcesByKeyMap).length > 0;
  }

  get itemsForCreateOrUpdate() {
    return Object.values(this.editedResourcesByKeyMap);
  }

  fieldValue(resource, fieldName) {
    let editedResource = this.#getEditedResource(resource);
    return valueFromPath(editedResource, fieldName);
  }

  @action
  setFieldValue(resource, fieldName, fieldValue, filters) {
    const key = this.getKey(resource);

    if (!this.originalResourcesByKeyMap[key]) {
      this.originalResourcesByKeyMap[key] = safeClone(resource);
    }

    const source = this.editedResourcesByKeyMap[key] ?? resource;
    const updatedResource = safeClone(source);
    const originalResource = this.originalResourcesByKeyMap[key];

    setValueForPath(updatedResource, fieldName, fieldValue);

    if (filters !== undefined) {
      updatedResource.filters =
        typeof filters === 'string' ? JSON.parse(filters || '{}') : filters;
    }

    const cleanedUpdated = normalizeForComparison(
      updatedResource,
      originalResource
    );
    const cleanedOriginal = normalizeForComparison(
      originalResource,
      originalResource
    );

    const changes = diff(cleanedOriginal, cleanedUpdated);

    if (changes.length === 0) {
      const newMap = { ...this.editedResourcesByKeyMap };
      delete newMap[key];
      this.editedResourcesByKeyMap = newMap;

      const originals = { ...this.originalResourcesByKeyMap };
      delete originals[key];
      this.originalResourcesByKeyMap = originals;
    } else {
      this.editedResourcesByKeyMap = {
        ...this.editedResourcesByKeyMap,
        [key]: updatedResource,
      };
    }
  }

  getKey(resource) {
    return this.keyProperty.reduce(
      (value, keyPart) => value?.[keyPart],
      resource
    );
  }

  #getEditedResource(resource) {
    return this.editedResourcesByKeyMap[this.getKey(resource)] || resource;
  }

  reset() {
    this.editedResourcesByKeyMap = {};
    this.originalResourcesByKeyMap = {};
  }
}
